import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logger } from '@/utils/logger';
import {
  Product,
  Phase,
  PhaseDisplayNames,
  PhaseColors,
} from '../../types/bom';
import { listProducts, getPhaseSummary, listRevisions } from '../../services/bomService';
import BOMImportDialog from './BOMImportDialog';

const PCBModuleList: React.FC = () => {
  const { t } = useTranslation('bom');
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [phaseSummary, setPhaseSummary] = useState<Record<Phase, number>>({} as Record<Phase, number>);
  const [selectedPhase, setSelectedPhase] = useState<Phase | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // BOM Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [importProductId, setImportProductId] = useState<string | null>(null);
  const [importProductRevision, setImportProductRevision] = useState<string | null>(null);
  const [importProductRevisions, setImportProductRevisions] = useState<{ revision: string; status: string }[]>([]);

  useEffect(() => {
    loadData();
  }, [selectedPhase]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [productsData, summaryData] = await Promise.all([
        listProducts(selectedPhase || undefined),
        getPhaseSummary(),
      ]);
      setProducts(productsData || []);
      setPhaseSummary(summaryData || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load PCB modules');
    } finally {
      setLoading(false);
    }
  };

  const phases: Phase[] = ['pre_bom', 'evt', 'dvt', 'pvt', 'mp'];

  const handleOpenImport = async (product: Product) => {
    try {
      const revisions = await listRevisions(product.product_id);
      setImportProductId(product.product_id);
      setImportProductRevision(product.current_revision);
      setImportProductRevisions(
        revisions.map((r: { revision: string; status: string }) => ({ revision: r.revision, status: r.status }))
      );
      setShowImportDialog(true);
    } catch (err) {
      logger.error('Failed to load revisions:', err);
      // Still open dialog, just without revision list
      setImportProductId(product.product_id);
      setImportProductRevision(product.current_revision);
      setImportProductRevisions([]);
      setShowImportDialog(true);
    }
  };

  const handleImportComplete = () => {
    loadData(); // Refresh the list after import
  };

  const handleSelectProduct = async (product: Product) => {
    try {
      const revisions = await listRevisions(product.product_id);
      setImportProductId(product.product_id);
      setImportProductRevision(product.current_revision);
      setImportProductRevisions(
        revisions.map((r: { revision: string; status: string }) => ({ revision: r.revision, status: r.status }))
      );
      setShowProductSelector(false);
      setShowImportDialog(true);
    } catch (err) {
      logger.error('Failed to load revisions:', err);
      setImportProductId(product.product_id);
      setImportProductRevision(product.current_revision);
      setImportProductRevisions([]);
      setShowProductSelector(false);
      setShowImportDialog(true);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('pcbModules.title', 'PCB Modules')}
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowProductSelector(true)}
            className="px-4 py-2 border border-green-500 text-green-600 rounded-lg hover:bg-green-50 dark:border-green-400 dark:text-green-400 dark:hover:bg-green-900/20 transition-colors"
          >
            {t('bom.importBOM', 'Import BOM')}
          </button>
          <button
            onClick={() => navigate('/app/bom/pcb-modules/new')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('pcbModules.newModule', 'New PCB Module')}
          </button>
        </div>
      </div>

      {/* Phase Summary Cards */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        {phases.map((phase) => (
          <button
            key={phase}
            onClick={() => setSelectedPhase(selectedPhase === phase ? '' : phase)}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedPhase === phase
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}
          >
            <div className={`w-3 h-3 rounded-full ${PhaseColors[phase]} mb-2`} />
            <div className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {PhaseDisplayNames[phase]}
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {phaseSummary[phase] || 0}
            </div>
          </button>
        ))}
      </div>

      {/* PCB Modules Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <div className="flex justify-center mb-4">
            <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
            {t('pcbModules.errorLoading', 'Failed to load PCB modules')}
          </h3>
          <p className="text-red-600 dark:text-red-300 text-sm mb-4">{error}</p>
          {error.includes('database') && error.includes('does not exist') && (
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {t('firestoreNotConfigured', 'Firestore database is not configured. Please set up Firestore in the Google Cloud Console.')}
            </p>
          )}
          <button
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('retry', 'Retry')}
          </button>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          {t('pcbModules.noModules', 'No PCB modules found')}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('pcbModules.moduleCode', 'Module Code')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('pcbModules.moduleName', 'Module Name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('category', 'Category')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('phase', 'Phase')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('revision', 'Revision')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('status', 'Status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  {t('actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {products.map((product) => (
                <tr
                  key={product.product_id}
                  onClick={() => navigate(`/app/bom/pcb-modules/${product.product_id}`)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {product.product_code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {product.product_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {product.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${
                        PhaseColors[product.current_phase]
                      }`}
                    >
                      {PhaseDisplayNames[product.current_phase]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {product.current_revision}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        product.status === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : product.status === 'on_hold'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {product.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenImport(product);
                      }}
                      className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 text-sm font-medium"
                    >
                      {t('bom.importBOM', 'Import BOM')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Product Selector Dialog */}
      {showProductSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('bom.selectProductToImport', 'Select PCB Module to Import BOM')}
              </h2>
              <button
                onClick={() => setShowProductSelector(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto max-h-[60vh]">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              ) : products.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  {t('bom.noProductsFound', 'No PCB modules found. Create a module first.')}
                </div>
              ) : (
                <div className="space-y-2">
                  {products.map((product) => (
                    <button
                      key={product.product_id}
                      onClick={() => handleSelectProduct(product)}
                      className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <div className="font-medium text-gray-900 dark:text-white">
                        {product.product_code}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {product.product_name} • Rev {product.current_revision}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BOM Import Dialog */}
      {importProductId && (
        <BOMImportDialog
          isOpen={showImportDialog}
          onClose={() => {
            setShowImportDialog(false);
            setImportProductId(null);
          }}
          productId={importProductId}
          currentRevision={importProductRevision || undefined}
          revisions={importProductRevisions}
          onImportComplete={handleImportComplete}
        />
      )}
    </div>
  );
};

export default PCBModuleList;
